/**
 * SOLUTION DÉFINITIVE - Gestionnaire de déconnexion robuste
 * 
 * Ce script intercepte TOUS les clics de déconnexion et force une redirection
 * fiable vers /account/login pour éliminer les boucles de redirection.
 * 
 * Fonctionnalités :
 * - Interception de tous les liens de déconnexion
 * - Déconnexion via API Shopify
 * - Redirection forcée côté client
 * - Protection anti-boucle robuste
 * - Compatible avec tous les navigateurs
 */

class LogoutHandler {
  constructor() {
    this.STORAGE_KEY = 'croux_logout_protection';
    this.MAX_LOGOUT_ATTEMPTS = 3;
    this.LOGOUT_COOLDOWN = 5000; // 5 secondes
    
    this.init();
  }

  init() {
    // Attendre que le DOM soit chargé
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupLogoutInterception());
    } else {
      this.setupLogoutInterception();
    }
  }

  setupLogoutInterception() {
    // Intercepter TOUS les liens de déconnexion existants et futurs
    this.interceptLogoutLinks();
    
    // Observer les changements dans le DOM pour les liens ajoutés dynamiquement
    this.observeNewLogoutLinks();
    
    // Nettoyer les données de protection lors du chargement de la page de login
    this.cleanupOnLoginPage();
  }

  interceptLogoutLinks() {
    // Sélecteurs pour tous les types de liens de déconnexion
    const logoutSelectors = [
      'a[href*="/account/logout"]',
      'a[href*="account_logout"]',
      'a[href*="logout"]',
      '.logout-link',
      '.header-b2b__nav-item[href*="logout"]',
      '.header-b2b__mobile-menu-item[href*="logout"]',
      '.sidebar-nav__item[href*="logout"]',
      '.mobile-menu__item[href*="logout"]'
    ];

    logoutSelectors.forEach(selector => {
      const links = document.querySelectorAll(selector);
      links.forEach(link => this.attachLogoutHandler(link));
    });
  }

  observeNewLogoutLinks() {
    // Observer pour les liens ajoutés dynamiquement
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Vérifier si le nouveau nœud est un lien de déconnexion
            if (this.isLogoutLink(node)) {
              this.attachLogoutHandler(node);
            }
            
            // Vérifier les liens de déconnexion dans les enfants
            const logoutLinks = node.querySelectorAll ? 
              node.querySelectorAll('a[href*="logout"], a[href*="account_logout"]') : [];
            logoutLinks.forEach(link => this.attachLogoutHandler(link));
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  isLogoutLink(element) {
    if (element.tagName !== 'A') return false;
    
    const href = element.getAttribute('href') || '';
    const classList = element.className || '';
    
    return href.includes('logout') || 
           href.includes('account_logout') || 
           classList.includes('logout');
  }

  attachLogoutHandler(link) {
    // Éviter d'attacher plusieurs handlers sur le même lien
    if (link.hasAttribute('data-logout-handler')) return;
    
    link.setAttribute('data-logout-handler', 'true');
    
    // Sauvegarder l'URL originale pour référence
    link.setAttribute('data-original-href', link.href);
    
    link.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      console.log('🛍️ Clic de déconnexion intercepté:', link.href);
      this.handleLogout();
    });
  }

  async handleLogout() {
    try {
      // Vérifier la protection anti-boucle
      if (!this.isLogoutAllowed()) {
        console.warn('Logout bloqué : trop de tentatives récentes');
        return;
      }

      // Enregistrer la tentative de déconnexion
      this.recordLogoutAttempt();
      
      // Afficher un indicateur de chargement
      this.showLogoutIndicator();

      // Effectuer la déconnexion via l'API Shopify
      await this.performLogout();
      
      // Forcer la redirection vers la page de login
      this.redirectToLogin();
      
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // En cas d'erreur, rediriger quand même vers login
      this.redirectToLogin();
    }
  }

  isLogoutAllowed() {
    try {
      const data = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
      const now = Date.now();
      
      // Nettoyer les anciennes tentatives
      if (data.timestamp && (now - data.timestamp) > this.LOGOUT_COOLDOWN) {
        localStorage.removeItem(this.STORAGE_KEY);
        return true;
      }
      
      return (data.attempts || 0) < this.MAX_LOGOUT_ATTEMPTS;
    } catch (e) {
      return true;
    }
  }

  recordLogoutAttempt() {
    try {
      const data = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
      const now = Date.now();
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
        attempts: (data.attempts || 0) + 1,
        timestamp: now
      }));
    } catch (e) {
      // Ignorer les erreurs de localStorage
    }
  }

  showLogoutIndicator() {
    // Créer un indicateur visuel de déconnexion en cours
    const indicator = document.createElement('div');
    indicator.id = 'logout-indicator';
    indicator.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        font-family: Arial, sans-serif;
      ">
        <div style="
          background: white;
          padding: 20px 40px;
          border-radius: 8px;
          text-align: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        ">
          <div style="
            width: 20px;
            height: 20px;
            border: 2px solid #ccd200;
            border-top: 2px solid transparent;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 10px;
          "></div>
          <p style="margin: 0; color: #333;">Déconnexion en cours...</p>
        </div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    
    document.body.appendChild(indicator);
  }

  async performLogout() {
    console.log('🛍️ Début de la déconnexion...');
    
    // Méthode 1 : Utiliser l'URL de déconnexion Shopify classique
    try {
      // Récupérer le token CSRF si disponible
      const csrfToken = document.querySelector('meta[name="csrf-token"]');
      const authenticity_token = csrfToken ? csrfToken.getAttribute('content') : null;
      
      // Préparer les données du formulaire
      const formData = new FormData();
      if (authenticity_token) {
        formData.append('authenticity_token', authenticity_token);
      }
      
      const response = await fetch('/account/logout', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      console.log('🛍️ Réponse de déconnexion:', response.status);
      
      // Attendre un court délai pour que la déconnexion soit effective
      await new Promise(resolve => setTimeout(resolve, 800));
      
    } catch (error) {
      console.warn('🛍️ Déconnexion via fetch échouée, utilisation de la méthode alternative:', error);
      
      // Méthode alternative : créer un formulaire de déconnexion invisible
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/account/logout';
      form.style.display = 'none';
      
      // Ajouter un champ authenticity_token si disponible
      const token = document.querySelector('meta[name="csrf-token"]');
      if (token) {
        const tokenInput = document.createElement('input');
        tokenInput.type = 'hidden';
        tokenInput.name = 'authenticity_token';
        tokenInput.value = token.getAttribute('content');
        form.appendChild(tokenInput);
      }
      
      document.body.appendChild(form);
      form.submit();
      
      // Attendre que le formulaire soit soumis
      await new Promise(resolve => setTimeout(resolve, 1200));
    }
  }

  redirectToLogin() {
    // Nettoyer l'indicateur de chargement
    const indicator = document.getElementById('logout-indicator');
    if (indicator) {
      indicator.remove();
    }
    
    // Nettoyer les données de redirection anciennes
    this.cleanupRedirectData();
    
    // Forcer la redirection vers la page de login avec paramètre de succès
    const loginUrl = '/account/login?logout=success';
    
    // Utiliser plusieurs méthodes pour garantir la redirection
    try {
      // Méthode 1 : window.location.href
      window.location.href = loginUrl;
      
      // Méthode 2 : window.location.replace (au cas où)
      setTimeout(() => {
        window.location.replace(loginUrl);
      }, 100);
      
      // Méthode 3 : Redirection forcée en dernier recours
      setTimeout(() => {
        if (window.location.pathname !== '/account/login') {
          window.location.assign(loginUrl);
        }
      }, 500);
      
    } catch (error) {
      console.error('Erreur de redirection:', error);
      // En dernier recours, recharger la page avec l'URL de login
      window.location = loginUrl;
    }
  }

  cleanupRedirectData() {
    // Nettoyer toutes les données de redirection qui pourraient causer des boucles
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.includes('redirect') || 
            key.includes('shopify') || 
            key.includes('croux')) {
          sessionStorage.removeItem(key);
        }
      });
      
      // Nettoyer également localStorage
      const localKeys = Object.keys(localStorage);
      localKeys.forEach(key => {
        if (key.includes('redirect') && !key.includes('logout_protection')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      // Ignorer les erreurs de storage
    }
  }

  cleanupOnLoginPage() {
    // Si nous sommes sur la page de login, nettoyer les données de protection
    if (window.location.pathname === '/account/login') {
      setTimeout(() => {
        localStorage.removeItem(this.STORAGE_KEY);
      }, 2000);
    }
  }
}

// Initialiser le gestionnaire de déconnexion
window.CrouxLogoutHandler = new LogoutHandler();

// Export pour utilisation dans d'autres scripts si nécessaire
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LogoutHandler;
}